import React, { ReactNode } from "react";

import {
  Grid,
  Box,
  Modal as ModalMI,
  IconButton,
  ModalProps,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Close as CloseIcon, InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { WarningAmberRounded as WarningAmberRoundedIcon } from "@mui/icons-material/";
import { colors } from "@zapalacorp/tokens";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiInfo, FiShare2 } from "react-icons/fi";
import { HiOutlineEmojiSad } from "react-icons/hi";
import { IoCloseCircleOutline, IoWarningOutline } from "react-icons/io5";
import { MdOutlineChangeCircle } from "react-icons/md";
import { PiWarningOctagon } from "react-icons/pi";

import { Status } from "../../../util/enums";
import Button, { Color } from "../../Form/Button";
import Select from "../../Form/Select";
import ActionButtons, { ActionButtonProps } from "../ActionButtons";
import { AppSwitch } from "../AppSwitch";
import useStyles, { DescriptionText } from "./styles";

export enum ModalType {
  CONFIRMATION,
  LEAVE,
  DELETE,
  INFO,
  SHARE,
  CANCEL,
  LOGOUT,
  WARNING,
  ERROR_FOUND,
}

export interface StatusFieldProps {
  label: string;
  value: string;
  dataCy?: string;
  disabled?: boolean;
  placeholder: string;
  handleChange: (value: string) => void;
}

export interface StatusButtonProps {
  status: string;
  action: (status: string) => void;
}

export interface StatusSwitchProps {
  checked: boolean;
  handleChange: (value: boolean, status: Status) => void;
}

interface Props extends Omit<ModalProps, "children" | "title"> {
  handleClose?: (event?: any, reason?: "backdropClick" | "escapeKeyDown") => void;
  open: boolean;
  title?: JSX.Element | string;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  leftIcon?: ReactNode;
  type?: ModalType;
  labelStatus?: string;
  showStatusField?: boolean;
  statusFieldProps?: StatusFieldProps;
  showBranchesField?: boolean;
  renderBranchesField?: () => ReactNode;
  showActionButtons?: boolean;
  hasActionBackground?: boolean;
  actionButtonProps?: ActionButtonProps;
  showLeftActionButtons?: boolean;
  actionLeftButtonProps?: ActionButtonProps;
  dataCyTitle?: string;
  dataCyCloseButton?: string;
  showModalBodyScroll?: boolean;
  "data-cy"?: string;

  showButtonStatus?: boolean;
  showModalFooter?: boolean;
  statusButtonProps?: StatusButtonProps;
  customHeaderComponent?: React.ReactNode;
  customRightButtons?: ReactNode;
  customLeftButtons?: ReactNode;

  showStatusSwitch?: boolean;
  statusSwitchProps?: StatusSwitchProps;

  maxWidth?: number | string;
  width?: number | string;
  modalFooterCustomClass?: string;
  description?: string;
}

const Modal = (props: Props) => {
  const {
    open,
    handleClose,
    title,
    type,
    children,
    className,
    bodyClassName,
    showActionButtons,
    actionButtonProps,
    showLeftActionButtons,
    actionLeftButtonProps,
    dataCyTitle,
    dataCyCloseButton,
    showStatusField,
    statusFieldProps,
    showBranchesField,
    renderBranchesField,
    showModalBodyScroll = true,
    showButtonStatus = false,
    showModalFooter = true,
    statusButtonProps,
    customHeaderComponent,
    customRightButtons,
    customLeftButtons,
    showStatusSwitch,
    statusSwitchProps,
    hasActionBackground = true,
    maxWidth,
    width,
    modalFooterCustomClass,
    description,
    ...rest
  } = props;

  const classes = useStyles({
    hasActionBackground,
    hasActionInLeft: showButtonStatus || showLeftActionButtons || showStatusSwitch || false,
  })();
  const { t: translate } = useTranslation();

  const getLeftIconByModalType = () => {
    switch (type) {
      case ModalType.CONFIRMATION:
        return (
          <div className={classnames(classes.containerIcon, classes.containerConfirmationIcon)}>
            <InfoOutlinedIcon htmlColor="#12B76A" />
          </div>
        );
      case ModalType.LEAVE:
        return (
          <div className={classnames(classes.containerIcon, classes.containerLeaveIcon)}>
            <WarningAmberRoundedIcon htmlColor="#F79009" />
          </div>
        );
      case ModalType.DELETE:
        return (
          <div className={classnames(classes.containerIcon, classes.containerDeleteIcon)}>
            <IoWarningOutline color="#F04438" size={20} />
          </div>
        );
      case ModalType.INFO:
        return (
          <div className={classnames(classes.containerIcon, classes.containerInfoIcon)}>
            <FiInfo color="#308CDB" size={18} />
          </div>
        );
      case ModalType.SHARE:
        return (
          <div className={classnames(classes.containerIcon, classes.containerInfoIcon)}>
            <FiShare2 size={22} color="#308CDB" />
          </div>
        );
      case ModalType.CANCEL:
        return (
          <div className={classnames(classes.containerIcon, classes.containerDeleteIcon)}>
            <IoCloseCircleOutline size={22} color="#D92D20" />
          </div>
        );

      case ModalType.LOGOUT:
        return (
          <div className={classnames(classes.containerIcon, classes.containerInfoIcon)}>
            <HiOutlineEmojiSad size={22} color="#3377B6" />
          </div>
        );

      case ModalType.WARNING:
        return (
          <div className={classnames(classes.containerIcon, classes.containerWarningIcon)}>
            <PiWarningOctagon size={22} color="#F79009" />
          </div>
        );

      case ModalType.ERROR_FOUND:
        return (
          <div className={classnames(classes.containerIcon, classes.containerErrorFoundIcon)}>
            <WarningAmberRoundedIcon htmlColor={colors.error500} />
          </div>
        );
      default:
        return null;
    }
  };

  const renderLeftButtons = () => {
    if (showLeftActionButtons) {
      return customLeftButtons || <ActionButtons {...actionLeftButtonProps} />;
    }

    if (showStatusSwitch)
      return (
        <div className={classes.switchContainer}>
          <span className={classes.switchTitle}>{translate("active")}</span>
          <AppSwitch
            checked={statusSwitchProps?.checked}
            onChange={(_: any, checked: boolean) =>
              statusSwitchProps?.handleChange(checked, checked ? Status.ACTIVE : Status.INACTIVE)
            }
          />
        </div>
      );

    if (showButtonStatus)
      return (
        <div className={classes.buttonStatusWrapper}>
          <label className={classes.buttonStatusLabel}>{translate("actualStatus")}:</label>
          <Button
            className={classnames(
              classes.buttonStatus,
              statusButtonProps?.status === Status.ACTIVE
                ? classes.buttonActive
                : classes.buttonInactive
            )}
            customColor={statusButtonProps?.status === Status.ACTIVE ? Color.SUCCESS : Color.ERROR}
            onClick={() =>
              statusButtonProps?.action(
                statusButtonProps?.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE
              )
            }
          >
            <MdOutlineChangeCircle size={16} className="mr-2" />
            {statusButtonProps?.status}
          </Button>
        </div>
      );

    return null;
  };

  return (
    <ModalMI open={open} onClose={handleClose} {...rest}>
      <div
        data-cy={rest["data-cy"]}
        className={classnames(className, classes.paper)}
        style={{ maxWidth, width }}
      >
        <div className={classes.modalBody}>
          <Grid container direction="row" className={classes.header} id="modalHeader">
            <Grid item xs={12}>
              {customHeaderComponent || (
                <div className={classes.container}>
                  <div className={classes.titleContainer}>
                    {getLeftIconByModalType()}
                    <Box className={classes.leftBox}>
                      <span className={classes.title} data-cy={dataCyTitle}>
                        {title}
                      </span>
                      <DescriptionText>{description}</DescriptionText>
                    </Box>
                  </div>

                  <Box>
                    {handleClose && (
                      <IconButton onClick={handleClose} data-cy={dataCyCloseButton}>
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    )}
                  </Box>
                </div>
              )}
            </Grid>
          </Grid>

          <Grid
            id="modalBody"
            container
            direction="row"
            className={showModalBodyScroll ? classes.body : classes.bodyWithoutScroll}
          >
            {children}

            {showStatusField && (
              <Grid
                className={bodyClassName}
                style={{ width: showBranchesField ? "100%" : undefined }}
              >
                <div
                  className={classes.statusContainer}
                  style={{ width: showBranchesField ? "100%" : undefined }}
                >
                  {showBranchesField && renderBranchesField ? (
                    <div className={classes.statusAndBranchContainer}>
                      <div>
                        <Select
                          className={classes.select}
                          name="statusField"
                          data-cy={statusFieldProps?.dataCy}
                          displayEmpty
                          {...statusFieldProps}
                          onChange={(event: SelectChangeEvent<any>) =>
                            statusFieldProps?.handleChange(event.target.value)
                          }
                        >
                          <MenuItem value={Status.ACTIVE} data-cy={Status.ACTIVE}>
                            {translate("active")}
                          </MenuItem>
                          <MenuItem value={Status.INACTIVE} data-cy={Status.INACTIVE}>
                            {translate("inactive")}
                          </MenuItem>
                        </Select>
                      </div>

                      <div className={classes.selectBranchContainer}>{renderBranchesField()}</div>
                    </div>
                  ) : (
                    <Select
                      className={classes.select}
                      name="statusField"
                      data-cy={statusFieldProps?.dataCy}
                      displayEmpty
                      {...statusFieldProps}
                      onChange={(event: SelectChangeEvent<any>) =>
                        statusFieldProps?.handleChange(event.target.value)
                      }
                    >
                      <MenuItem value={Status.ACTIVE} data-cy={Status.ACTIVE}>
                        {translate("active")}
                      </MenuItem>
                      <MenuItem value={Status.INACTIVE} data-cy={Status.INACTIVE}>
                        {translate("inactive")}
                      </MenuItem>
                    </Select>
                  )}
                </div>
              </Grid>
            )}
          </Grid>

          {showModalFooter && (
            <div
              className={classnames(classes.modalFooter, modalFooterCustomClass)}
              id="modalFooter"
            >
              {renderLeftButtons()}
              {showActionButtons && (
                <ActionButtons
                  onCancel={actionButtonProps?.onCancel ?? handleClose}
                  {...actionButtonProps}
                />
              )}
              {customRightButtons}
            </div>
          )}
        </div>
      </div>
    </ModalMI>
  );
};

export default Modal;
